import { RefObject, useCallback } from 'react'
import { useDragTreeStyle } from '../../../containers/BulkSheetView/hooks/gridEvents/treeRowDrag'
import {
  ProductBacklogRow,
  isSprintRow,
  isProductBacklogItemRow,
  ProductBacklogItemRow,
} from '../productBacklog'
import { GridApi, RowDragEvent, GridOptions } from 'ag-grid-community'
import {
  slideRowsAfter,
  slideRowsBefore,
} from '../../../containers/BulkSheetView/hooks/actions/moveTreeRows'
import { SwitchSprint } from './sprintManager'

type DragAndDropCallbacks = Pick<
  GridOptions,
  'onRowDragEnter' | 'onRowDragMove' | 'onRowDragEnd' | 'onRowDragLeave'
>

export const useProductBacklogDragAndDrop = (
  ref: RefObject<HTMLDivElement>,
  data: ProductBacklogRow[],
  setData: (data: ProductBacklogRow[]) => void,
  switchSprint: SwitchSprint,
  gridApi?: GridApi,
  updateContext?: (partial: Record<string, any>) => void,
  contextRef?: React.MutableRefObject<Record<string, any>>
): DragAndDropCallbacks => {
  const acceptChild = useCallback(
    (parent: ProductBacklogRow, child: ProductBacklogRow): boolean => {
      return isSprintRow(parent) && isProductBacklogItemRow(child)
    },
    []
  )
  const dragStyle = useDragTreeStyle(ref, acceptChild, gridApi, updateContext!)
  const onRowDragEnd = useCallback(
    (event: RowDragEvent<ProductBacklogItemRow>) => {
      const movedIds = event.nodes.map(v => v.id)
      const overNode = event.overNode
      const ctx = contextRef?.current
      if (!ctx || !overNode || overNode.id !== ctx.draggableNodeId) {
        return
      }
      try {
        // ProductBacklogItemのみドラッグ可能
        const movedPbiRows = data.filter(
          v => movedIds.includes(v.uuid) && isProductBacklogItemRow(v)
        ) as ProductBacklogItemRow[]
        const overNodeData = data.find(v => v.uuid === overNode.id)

        if (movedPbiRows.length === 0 || !overNodeData) {
          return
        }

        if (ctx.onTree) {
          const targetSprintRowUuid = overNodeData.treeValue[0]
          switchSprint(targetSprintRowUuid, movedPbiRows)
          overNode.setExpanded(true)
        } else {
          // If moved toward down, move after overNode.
          if (event.overIndex > (event.node.rowIndex || 0)) {
            setData(slideRowsAfter(data, movedPbiRows, overNode.id!))
            return
          }
          setData(slideRowsBefore(data, movedPbiRows, overNode.id!))
        }
      } finally {
        // Clear drag cell style
        dragStyle.refreshDragStyle()
      }
    },
    [data, setData, switchSprint, dragStyle, contextRef]
  )

  return {
    onRowDragEnter: dragStyle.onRowDragEnter,
    onRowDragMove: dragStyle.onRowDragMove,
    onRowDragEnd,
    onRowDragLeave: dragStyle.refreshDragStyle,
  }
}
